import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronRight, Search, Settings2, Boxes, Save, X, ServerCrash, Lock, Wifi } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { useAdminData } from "@/hooks/useAdminData";
import useSettingsDrafts, { validateDraftBatch } from "@/hooks/useSettingsDrafts";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Modal } from "@/components/Modals";
import SettingBand from "@/components/admin/SettingBand";
import SettingsSaveBar from "@/components/admin/SettingsSaveBar";
import SettingsAuditPanel from "@/components/admin/SettingsAuditPanel";
import { OUTLET_SCOPE } from "@/components/admin/adminScope";
import {
  buildConfigTree, categoryNodeId, domainNodeId, firstCategory, locateSetting,
} from "@/components/admin/adminTree";
import { searchAdmin, ADMIN_MODULES, ADMIN_DOMAIN_ORDER } from "@/lib/settingsMap";
import PrinterRegistry from "@/components/PrinterRegistry";
import PrintRoutingPanel from "@/components/admin/PrintRoutingPanel";
import PrintJobsPanel from "@/components/admin/PrintJobsPanel";
import StaffPanel from "@/components/admin/StaffPanel";
import RolesPanel from "@/components/admin/RolesPanel";
import ComboPanel from "@/components/admin/ComboPanel";
import FloorDesigner from "@/components/admin/FloorDesigner";
import ReportsPanel from "@/pages/ReportsPanelV2";
import SettingsPortability from "@/components/SettingsPortability";
import BackupsPanel from "@/components/admin/BackupsPanel";
import OrderListsPanel from "@/components/admin/OrderListsPanel";
import TelegramHub from "@/components/admin/TelegramHub";
import RemoteAccessPanel from "@/components/admin/RemoteAccessPanel";
import DeviceLogsPanel from "@/components/admin/DeviceLogsPanel";
import MenuItemDialog from "@/components/admin/MenuItemDialog";
import { NEW_ITEM } from "@/lib/adminMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const describeError = error =>
  error?.response?.data?.detail || error?.message || "Request failed";

/**
 * Mobile-only Administration console.
 *
 * The desktop AdminPage is a dense, layered two-column tree built for a mouse and a
 * wide screen. On a phone that layout collapses into an unreadable mess. This
 * component is the mobile counterpart: a bottom-tab shell (Settings / Modules / Save)
 * with an accordion domain browser and full-screen category + module drills. It is a
 * SEPARATE component — AdminPage (PC) is never touched. Both share the same data
 * (buildConfigTree, SettingBand, useSettingsDrafts, SettingsSaveBar, useAdminData)
 * and the SAME write path (PUT /api/settings/bulk at OUTLET scope), so a setting
 * saved on the phone and on the desktop are indistinguishable server-side.
 */
export default function AdminPageMobile() {
  const { token, user, permissions, settings, setSettings } = usePOS();
  const adminData = useAdminData(token);

  const canEditSettings = Array.isArray(permissions)
    ? permissions.some(name => /^settings\.group\..+\.edit$/.test(name))
    : user?.role !== "captain";

  const [metadata, setMetadata] = useState([]);
  const [resolvedContext, setResolvedContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new Set());
  const [selection, setSelection] = useState(null); // landing on mobile = domain accordion (no auto-open)
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [floorOpen, setFloorOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const scope = OUTLET_SCOPE;

  const authHeader = useMemo(() => ({ headers: token ? { Authorization: `Bearer ${token}` } : {} }), [token]);

  const loadSettings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data: registry } = await axios.get(`${API}/settings/metadata`, authHeader);
      setMetadata(registry);
      const { data } = await axios.get(`${API}/settings/resolved`, authHeader);
      setResolvedContext(data);
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setLoading(false);
    }
  }, [token, authHeader]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const saveBatch = async changes => {
    try {
      await axios.put(`${API}/settings/bulk`, {
        changes: Object.fromEntries(changes),
        context: { scope_type: "OUTLET", scope_id: null },
      }, authHeader);
      toast.success(changes.length === 1 ? "Setting saved" : `${changes.length} settings saved`);
      await loadSettings();
    } catch (error) {
      toast.error(describeError(error));
      throw error;
    }
  };

  const tree = useMemo(() => buildConfigTree(metadata), [metadata]);

  const toggleNode = useCallback(nodeId => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
      return next;
    });
  }, []);

  const openCategory = useCallback((domain, category) => {
    setSelection({ kind: "category", domain, category });
    setQuery("");
  }, []);

  const openModule = useCallback(id => { setSelection({ kind: "module", id }); setQuery(""); }, []);
  const openModules = useCallback(() => { setSelection({ kind: "modules" }); setQuery(""); }, []);

  const onPick = useCallback(hit => {
    setSearchOpen(false);
    const located = locateSetting(tree, hit.key);
    if (located) openCategory(located.domain, located.category);
    else if (hit.sectionId && hit.categoryId) openCategory(hit.sectionId, hit.categoryId);
  }, [tree, openCategory]);

  const resolvedScope = resolvedContext?.context?.scope;
  const resolving = loading || (!!resolvedScope && resolvedScope !== scope.type);

  const draftsApi = useSettingsDrafts(resolvedContext?.settings);
  const { dirtyKeys, isDirty, saveState, revert, commit, valueOf } = draftsApi;

  const metadataByKey = useMemo(() => {
    const map = {};
    for (const meta of metadata) map[meta.key] = meta;
    return map;
  }, [metadata]);

  const stagedChanges = useMemo(() => {
    const changes = {};
    for (const key of dirtyKeys) changes[key] = valueOf(key);
    return changes;
  }, [dirtyKeys, valueOf]);

  const validationProblems = useMemo(
    () => validateDraftBatch(stagedChanges, resolvedContext?.settings, metadataByKey),
    [stagedChanges, resolvedContext]
  );

  const dangerKeys = useMemo(
    () => dirtyKeys.filter(key =>
      metadataByKey[key]?.config_class === "financial" ||
      metadataByKey[key]?.requires_restart ||
      metadataByKey[key]?.requires_reload
    ),
    [dirtyKeys, metadataByKey]
  );

  const performSave = async () => {
    setConfirmOpen(false);
    try { await commit(saveBatch); }
    catch { /* bar carries the state */ }
  };

  const onSaveClick = needsConfirm => { if (needsConfirm) setConfirmOpen(true); else void performSave(); };

  // Ordered domains; modules come from ADMIN_MODULES.
  const orderedDomains = useMemo(() => {
    const present = tree.map(d => d.domain);
    const ordered = ADMIN_DOMAIN_ORDER.filter(d => present.includes(d));
    present.filter(d => !ADMIN_DOMAIN_ORDER.includes(d)).forEach(d => ordered.push(d));
    return ordered.map(name => tree.find(d => d.domain === name)).filter(Boolean);
  }, [tree]);

  if (selection?.kind === "category") {
    return (
      <CategoryView
        tree={tree}
        domain={selection.domain}
        category={selection.category}
        onBack={() => setSelection(null)}
        scope={scope}
        canEditSettings={canEditSettings}
        loading={loading}
        resolving={resolving}
        resolvedContext={resolvedContext}
        draftsApi={draftsApi}
        metadata={metadata}
        onSearch={() => setSearchOpen(true)}
        onGoSettings={() => setSelection(null)}
        onGoModules={() => openModules()}
        isDirty={isDirty}
        onSave={() => onSaveClick(dangerKeys.length > 0)}
        expanded={expanded}
        toggleNode={toggleNode}
        token={token}
      />
    );
  }

  if (selection?.kind === "modules") {
    return <ModuleListView onOpen={openModule} onBack={() => setSelection(null)} />;
  }

  if (selection?.kind === "module") {
    return (
      <ModuleView
        id={selection.id}
        token={token}
        adminData={adminData}
        settings={settings}
        setSettings={setSettings}
        editingItem={editingItem}
        setEditingItem={setEditingItem}
        floorOpen={floorOpen}
        setFloorOpen={setFloorOpen}
        onBack={openModules}
        canEditSettings={canEditSettings}
      />
    );
  }

  // ── Top-level mobile shell: tabs + domain accordion ──
  return (
    <div className="flex flex-col h-full bg-background" data-testid="admin-mobile-control-centre">
      {/* Header */}
      <header className="shrink-0 bg-card text-foreground border-b px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        <h1 className="text-title font-semibold">Administration</h1>
        <button
          className="w-11 h-11 -mr-2 flex items-center justify-center rounded-md hover:bg-muted"
          onClick={() => setSearchOpen(true)}
          aria-label="Search settings"
          data-testid="admin-mobile-search-button"
        >
          <Search className="w-5 h-5" />
        </button>
      </header>

      <div className="shrink-0 flex items-center gap-1.5 px-4 py-1.5 text-micro text-muted-foreground border-b bg-muted/30">
        <span>Applies to: <strong className="font-semibold text-foreground">This Outlet</strong></span>
      </div>

      <div className="flex-1 overflow-y-auto pb-24">
        {!canEditSettings && (
          <div className="flex items-start gap-2 m-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-micro text-amber-900" role="status">
            <Lock className="w-3.5 h-3.5 mt-px shrink-0" />
            <span>You can read these settings but not change them on this device. Ask an admin to make the change.</span>
          </div>
        )}

        {loading && <p className="px-4 py-10 text-center text-meta text-muted-foreground">Loading configuration…</p>}

        {!loading && !tree.length && (
          <div className="rounded-md border bg-card m-3 px-3 py-8 text-center text-meta text-muted-foreground">
            <ServerCrash className="w-6 h-6 mx-auto mb-2 opacity-40" />
            Settings could not be loaded from the server. Check that the server PC is on and connected.
          </div>
        )}

        {/* Domain accordion */}
        {orderedDomains.map(domain => {
          const open = expanded.has(domainNodeId(domain.domain));
          return (
            <section key={domain.domain} className="border-b border-border/60" data-testid={`admin-mobile-domain-${domain.domain.replace(/\s+/g, "-").toLowerCase()}`}>
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 text-body font-semibold"
                onClick={() => toggleNode(domainNodeId(domain.domain))}
                aria-expanded={open}
                data-testid={`admin-mobile-domain-toggle-${domain.domain.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <span>{domain.domain}</span>
                <span className="flex items-center gap-2 text-micro text-muted-foreground">
                  <span className="tabular-nums">{domain.categories.reduce((t, c) => t + c.count, 0)}</span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
                </span>
              </button>
              {open && (
                <div className="pb-2">
                  {domain.categories.map(category => (
                    <button
                      key={category.nodeId}
                      type="button"
                      className="w-full flex items-center justify-between px-6 py-2.5 text-meta hover:bg-muted/40"
                      onClick={() => openCategory(domain.domain, category.category)}
                      data-testid={`admin-mobile-category-${domain.domain.replace(/\s+/g, "-").toLowerCase()}-${category.category.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      <span className="text-left">{category.category}</span>
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
            </section>
          );
        })}

        {/* Modules entry */}
        <button
          type="button"
          className="w-full flex items-center justify-between px-4 py-3 mt-2 text-body font-semibold border-t border-border/60"
          onClick={() => openModules()}
          data-testid="admin-mobile-modules-entry"
        >
          <span className="flex items-center gap-2"><Boxes className="w-5 h-5 text-muted-foreground" /> Management</span>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      {/* Bottom tab bar */}
      <MobileTabBar
        isDirty={isDirty}
        dirtyCount={dirtyKeys.length}
        onSave={() => onSaveClick(dangerKeys.length > 0)}
        onModules={() => openModules()}
        onSettings={() => setSelection(null)}
      />

      {searchOpen && (
        <SearchSheet
          query={query}
          setQuery={setQuery}
          registry={metadata}
          onPick={onPick}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {canEditSettings && (
        <SettingsSaveBar
          dirtyKeys={dirtyKeys}
          metadataByKey={metadataByKey}
          saveState={saveState}
          problems={validationProblems}
          dangerKeys={dangerKeys}
          onSave={onSaveClick}
          onDiscard={revert}
          confirmOpen={confirmOpen}
          onConfirmOpenChange={setConfirmOpen}
          onConfirmSave={performSave}
        />
      )}
    </div>
  );
}

/* ── Category drill view ──────────────────────────────────────────── */

function CategoryView({ tree, domain, category, onBack, scope, canEditSettings, loading, resolving, resolvedContext, draftsApi, metadata, onSearch, onGoSettings, onGoModules, isDirty, onSave, expanded, toggleNode, token }) {
  const { dirtyKeys, valueOf, setDraft, focusKey, clearFocus } = draftsApi;
  const dom = tree.find(d => d.domain === domain);
  const cat = dom?.categories.find(c => c.category === category) || null;

  return (
    <div className="flex flex-col h-full bg-background" data-testid={`admin-mobile-category-${domain.replace(/\s+/g, "-").toLowerCase()}-${category.replace(/\s+/g, "-").toLowerCase()}`}>
      <header className="shrink-0 bg-card text-foreground border-b px-2 py-3 flex items-center gap-2 sticky top-0 z-20">
        <button className="w-11 h-11 flex items-center justify-center rounded-md hover:bg-muted" onClick={onBack} aria-label="Back" data-testid="admin-mobile-category-back">
          <ChevronRight className="w-5 h-5 rotate-180" />
        </button>
        <div className="min-w-0">
          <div className="text-micro uppercase tracking-wider opacity-80">{domain}</div>
          <div className="text-body font-bold leading-tight truncate">{category}</div>
        </div>
        <button className="w-11 h-11 ml-auto flex items-center justify-center rounded-md hover:bg-muted" onClick={onSearch} aria-label="Search" data-testid="admin-mobile-category-search">
          <Search className="w-5 h-5" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-3 space-y-3 pb-24">
        {resolving && <p className="px-1 py-6 text-meta text-muted-foreground">Resolving configuration for {scope.label}…</p>}
        {!resolving && cat?.bands.map(band => (
          <SettingBand
            key={band.nodeId}
            band={band}
            open={band.id === "normal" || expanded.has(band.nodeId)}
            onToggle={() => toggleNode(band.nodeId)}
            resolved={resolvedContext?.settings}
            valueOf={valueOf}
            dirtyKeys={dirtyKeys}
            onDraft={setDraft}
            readOnly={!canEditSettings}
            focusKey={focusKey}
            onTouched={clearFocus}
          />
        ))}
        {!resolving && <SettingsAuditPanel token={token} />}
      </div>
    </div>
  );
}
/* ── Management: every section, one tap each ──────────────────────── */

function ModuleListView({ onOpen, onBack }) {
  return (
    <div className="flex flex-col h-full bg-background" data-testid="admin-mobile-module-list">
      <header className="shrink-0 bg-card text-foreground border-b px-2 py-3 flex items-center gap-2 sticky top-0 z-20">
        <button className="w-11 h-11 flex items-center justify-center rounded-md hover:bg-muted" onClick={onBack} aria-label="Back" data-testid="admin-mobile-module-list-back">
          <ChevronRight className="w-5 h-5 rotate-180" />
        </button>
        <div className="text-body font-bold leading-tight">Management</div>
      </header>
      <div className="flex-1 overflow-y-auto divide-y pb-24">
        {ADMIN_MODULES.map(m => (
          <button key={m.id} type="button" onClick={() => onOpen(m.id)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40"
                  data-testid={`admin-mobile-module-entry-${m.id}`}>
            <span className="min-w-0">
              <span className="block text-body font-semibold">{m.title}</span>
              <span className="block text-micro text-muted-foreground truncate">{m.summary}</span>
            </span>
            <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Module drill view ────────────────────────────────────────────── */

function ModuleView({ id, token, adminData, settings, setSettings, editingItem, setEditingItem, floorOpen, setFloorOpen, onBack, canEditSettings }) {
  const module = ADMIN_MODULES.find(m => m.id === id);
  const title = module?.title || "Management";

  let body = null;
  if (id === "menu") {
    body = (
      <div className="space-y-3">
        <div className="rounded-md border bg-card" data-testid="mobile-menu-panel">
          <div className="flex items-center justify-between gap-3 border-b px-3 py-2">
            <div>
              <h3 className="text-body font-bold">Menu catalogue</h3>
              <p className="text-micro text-muted-foreground tabular-nums">{adminData.menuItems?.length || 0} items · {adminData.menu?.length || 0} categories</p>
            </div>
            {/* NEW_ITEM, not `{}`: a truthy object reads as "edit this item" in the
                dialog, which then showed "Edit undefined" and threw on save. */}
            <Button variant="action" size="sm" className="h-11 px-4 text-body" onClick={() => setEditingItem(NEW_ITEM)} data-testid="mobile-add-menu-item">Add item</Button>
          </div>
          {!adminData.menuItems ? (
            <p className="px-3 py-6 text-center text-meta text-muted-foreground">Loading menu…</p>
          ) : (
            <div className="divide-y max-h-[55vh] overflow-y-auto">
              {adminData.menuItems.map(item => (
                <button key={item.id} className="w-full text-left px-3 py-2.5 hover:bg-muted/40 flex justify-between items-center" onClick={() => setEditingItem(item)} data-testid={`mobile-menu-item-${item.id}`}>
                  <span className="font-medium">{item.name}</span>
                  <span className="text-meta text-muted-foreground tabular-nums">₹{item.price}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {/* onSave was missing here too — the same silent crash as the desktop console. */}
        {editingItem && (
          <MenuItemDialog
            item={editingItem === NEW_ITEM ? null : editingItem}
            categories={adminData.menu}
            onSave={adminData.saveMenuItem}
            onClose={() => setEditingItem(null)}
          />
        )}
        <ComboPanel token={token} />
      </div>
    );
  } else if (id === "floor") {
    body = (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 rounded-md border bg-card px-3 py-2">
          <div><h3 className="text-body font-bold">Floor plan</h3><p className="text-micro text-muted-foreground">Design the spatial layout the Captain app displays.</p></div>
          <Button variant="outline" size="sm" className="h-11 px-4 text-body" onClick={() => setFloorOpen(true)}>Open</Button>
        </div>
        {floorOpen && <FloorDesigner token={token} onClose={() => setFloorOpen(false)} />}
      </div>
    );
  } else if (id === "printers") {
    body = <div className="space-y-3"><PrintJobsPanel /><PrinterRegistry /><div data-testid="mobile-print-routing"><PrintRoutingPanel /></div></div>;
  } else if (id === "staff") {
    body = <div className="space-y-3"><StaffPanel staff={adminData.staff} onSave={adminData.saveStaff} onDeactivate={adminData.deactivateStaff} onRemove={adminData.removeStaff} /><RolesPanel catalog={adminData.catalog} rolePerms={adminData.rolePerms} onKindsChanged={adminData.reload} /></div>;
  } else if (id === "reports") {
    body = <ReportsPanel />;
  } else if (id === "data") {
    body = <div className="space-y-6"><SettingsPortability /><BackupsPanel /></div>;
  } else if (id === "lists") {
    body = <OrderListsPanel />;
  } else if (id === "integrations") {
    body = <div className="space-y-4"><RemoteAccessPanel /><TelegramHub /></div>;
  } else if (id === "logs") {
    body = <DeviceLogsPanel />;
  } else if (id === "system") {
    body = (
      <div className="space-y-3">
        <div className="rounded-md border bg-card" data-testid="mobile-terminal-preferences">
          <div className="border-b px-3 py-2"><h3 className="text-body font-bold">Terminal preferences</h3><p className="text-micro text-muted-foreground">Stored on this till, not on the server.</p></div>
          <div className="flex items-center justify-between gap-3 px-3 py-3">
            <div>
              <div className="text-body font-semibold leading-tight">Production WebSocket sync</div>
              <div className="text-micro leading-tight text-muted-foreground mt-0.5">Turning this off isolates this till from the live order feed.</div>
            </div>
            <Switch checked={!!settings?.realtimeEnabled} onCheckedChange={value => setSettings(previous => ({ ...previous, realtimeEnabled: value }))} data-testid="mobile-realtime-switch" />
          </div>
        </div>
        <div className="rounded-md border bg-card px-3 py-4 text-meta text-muted-foreground flex items-center gap-2">
          <Wifi className="w-4 h-4" /> Live server health is available on the desktop console.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background" data-testid={`admin-mobile-module-${id}`}>
      <header className="shrink-0 bg-card text-foreground border-b px-2 py-3 flex items-center gap-2 sticky top-0 z-20">
        <button className="w-11 h-11 flex items-center justify-center rounded-md hover:bg-muted" onClick={onBack} aria-label="Back" data-testid="admin-mobile-module-back">
          <ChevronRight className="w-5 h-5 rotate-180" />
        </button>
        <div className="min-w-0"><div className="text-micro uppercase tracking-wider opacity-80">Management</div><div className="text-body font-bold leading-tight truncate">{title}</div></div>
      </header>
      <div className="flex-1 overflow-y-auto p-3 pb-24">{body}</div>
    </div>
  );
}

/* ── Bottom tab bar ───────────────────────────────────────────────── */

function MobileTabBar({ isDirty, dirtyCount, onSave, onModules, onSettings }) {
  return (
    <nav className="fixed bottom-0 inset-x-0 z-30 bg-card border-t border-border grid grid-cols-3 h-16" data-testid="admin-mobile-tabbar">
      <button className="flex flex-col items-center justify-center gap-0.5 text-micro font-semibold" onClick={onSettings} data-testid="admin-mobile-tab-settings">
        <Settings2 className="w-5 h-5" /><span>Settings</span>
      </button>
      <button className="flex flex-col items-center justify-center gap-0.5 text-micro font-semibold" onClick={onModules} data-testid="admin-mobile-tab-modules">
        <Boxes className="w-5 h-5" /><span>Modules</span>
      </button>
      <button
        className={`flex flex-col items-center justify-center gap-0.5 text-micro font-semibold ${isDirty ? "text-emerald-600" : "text-muted-foreground"}`}
        onClick={onSave}
        disabled={!isDirty}
        data-testid="admin-mobile-tab-save"
      >
        <Save className="w-5 h-5" /><span>{isDirty ? `Save (${dirtyCount})` : "Saved"}</span>
      </button>
    </nav>
  );
}

/* ── Search sheet ─────────────────────────────────────────────────── */

function SearchSheet({ query, setQuery, registry, onPick, onClose }) {
  const results = useMemo(() => searchAdmin(query, registry || []), [query, registry]);
  const shown = results.slice(0, 12);
  return (
    <Modal title="Search settings" onClose={onClose} testId="admin-mobile-search">
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 rounded-md border bg-background px-3 h-10">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input
            type="search"
            autoFocus
            className="flex-1 min-w-0 bg-transparent text-meta outline-none"
            placeholder="Try “footer”, “gst”, “font”, “discount”"
            value={query}
            onChange={e => setQuery(e.target.value)}
            data-testid="admin-mobile-search-input"
          />
          {query && <button className="text-muted-foreground" onClick={() => setQuery("")} aria-label="Clear"><X className="w-4 h-4" /></button>}
        </div>
        {query.trim().length >= 2 && (
          <div className="space-y-1 max-h-[55vh] overflow-y-auto">
            {!results.length && <p className="text-meta text-muted-foreground px-1">Nothing matches “{query}”.</p>}
            {shown.map(hit => (
              <button key={hit.id} type="button" className="w-full text-left px-3 py-2.5 rounded-md hover:bg-muted/40 border border-border/60" onClick={() => onPick(hit)} data-testid={`admin-mobile-search-result-${hit.id}`}>
                <strong className="block text-meta">{hit.label}</strong>
                <small className="text-micro text-muted-foreground">{hit.sectionTitle} · {hit.where}</small>
              </button>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}
